/**
 * Static Help / FAQ content for the in-app `/help` surface (Roadmap #14).
 *
 * SPEC: C:\iYuga\specs\help\SPEC-2026-06-13-help-faq.md
 *
 * Per the spec, the long-term source of truth is the markdown docs site
 * (help.iyuga.net). Until that exists and the in-app page pulls from it (or the
 * backend `modules/faq/` endpoint returns categorised content), this file is the
 * single, version-controlled source for the FAQ shown in the app.
 *
 * ── HOW THIS MAPS TO THE BACKEND ───────────────────────────────────────────
 * The backend `GET modules/faq/` endpoint returns a FLAT list of
 *   { id, question, answer }
 * (no categories). The Help page prefers live backend data when present and
 * falls back to this static content when the endpoint is empty/unavailable.
 * Backend rows without a category are grouped under "General".
 *
 * ── CONTENT STATUS ─────────────────────────────────────────────────────────
 * The 11 answers below are the finalized, on-brand copy from the design deck
 * (design-handoff/concepts/help-elevated/01-help-fable, Round 8 Stage 2). 7 are
 * final (`placeholder: false`). The remaining 4 — create-first-wheel,
 * add-content-to-wheel, customise-appearance, share-a-wheel — stay
 * `placeholder: true` (a "Draft" chip in the UI) because their underlying flows
 * are still being rebuilt; the copy is safe, high-level, and user-facing, with a
 * plain "more detail coming soon" note. No internal engineering markers appear in
 * any user-facing string (guarded by helpContent.leakGuard.test.js).
 *
 * Open items still owned by the team:
 *   1. The canonical docs-site base URL (DOCS_SITE_BASE below) once
 *      help.iyuga.net is live — the `docHref` links are forward-looking until then.
 *   2. Final GIF/screenshot URLs (the `media` slots accept a URL — null until
 *      assets exist).
 *   3. The owner who keeps this content current after launch.
 */

import { REFERRAL_PROGRAM_SUSPENDED } from "../constants/featureFlags";

/** Canonical docs site (help.iyuga.net — pending stand-up; docHref is forward-looking). */
export const DOCS_SITE_BASE = "https://help.iyuga.net";

/** Support inbox the contact form routes to (spec decision). */
export const SUPPORT_EMAIL = "support@iyuga.net";

/**
 * FAQ categories. Each category groups a set of articles. `icon` is optional
 * and intentionally omitted for now (the elevated view defines iconography);
 * the UI degrades gracefully without it.
 *
 * Article shape:
 *   id        – stable slug (used as anchor + React key)
 *   question  – the user-facing question / article title
 *   answer    – plain-text or lightweight-markup answer (string)
 *   keywords  – extra search terms not present in question/answer
 *   media     – optional { type: 'gif' | 'image', src, alt } (null until assets exist)
 *   docHref   – optional deep link into the docs site for the full write-up
 *   placeholder – true when the copy is still pending final confirmation (shows a
 *                 "Draft" chip); false when the answer is finalized
 */
export const HELP_CATEGORIES = [
  {
    id: "getting-started",
    title: "Getting started",
    description: "The basics of iYuga — accounts, your first Wheel, and the gallery.",
    articles: [
      {
        id: "what-is-a-wheel",
        question: "What is a Wheel?",
        answer:
          "A Wheel is iYuga's core building block — a shareable, interactive collection of your content (links, images, videos, documents, and more), organised into Segments. Build one, share it with the world, and others can clone it to make their own version.",
        keywords: ["wheel", "basics", "overview", "segment"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/getting-started/what-is-a-wheel`,
        placeholder: false,
      },
      {
        id: "create-first-wheel",
        question: "How do I create my first Wheel?",
        answer:
          "Head to your Gallery and tap Create Wheel, then follow the on-screen steps to shape your very first one. We're putting the finishing touches on the builder experience, so a full step-by-step walkthrough is coming soon.",
        keywords: ["create", "build", "new wheel", "first", "builder"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/getting-started/create-your-first-wheel`,
        placeholder: true,
      },
      {
        id: "what-is-the-gallery",
        question: "What is my gallery?",
        answer:
          "Your Gallery is home to every Wheel you've created, cloned, unlocked, or favourited — and it doubles as your public profile, so anyone can browse what you've made.",
        keywords: ["gallery", "profile", "home", "my wheels"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/getting-started/the-gallery`,
        placeholder: false,
      },
    ],
  },
  {
    id: "building-wheels",
    title: "Building & customising Wheels",
    description: "Working in the builder — segments, media, layout, and styling.",
    articles: [
      {
        id: "add-content-to-wheel",
        question: "How do I add content to a Wheel?",
        answer:
          "Open any Wheel in the builder, then add Segments for whatever you'd like to include — links, images, videos, documents, audio, and more. We're refining the builder right now, so the exact steps here will get a full update soon.",
        keywords: ["add", "content", "segment", "media", "upload"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/building/adding-content`,
        placeholder: true,
      },
      {
        id: "customise-appearance",
        question: "Can I customise how my Wheel looks?",
        answer:
          "Yes — you can adjust colours, layout, wallpaper, and styling for each Wheel individually. We're still finalising the design controls, so look out for a complete walkthrough soon.",
        keywords: ["customise", "style", "colour", "color", "theme", "appearance"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/building/customising-appearance`,
        placeholder: true,
      },
    ],
  },
  {
    id: "sharing-cloning",
    title: "Sharing, cloning & remixing",
    description: "Getting your Wheels in front of people and the clone economy.",
    articles: [
      {
        id: "share-a-wheel",
        question: "How do I share a Wheel?",
        answer:
          "Use the Share option on any Wheel to grab a link or post it to social media. Public Wheels can also be discovered and cloned by other users. We're polishing the sharing experience, so a full guide is on its way.",
        keywords: ["share", "link", "public", "social"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/sharing/share-a-wheel`,
        placeholder: true,
      },
      {
        id: "clone-a-wheel",
        question: "What does it mean to clone a Wheel?",
        answer:
          "Cloning copies someone else's public Wheel into your own Gallery, so you can make it your own — customise it, remix it, reuse it. The original creator always stays credited.",
        keywords: ["clone", "copy", "remix", "duplicate"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/sharing/cloning`,
        placeholder: false,
      },
    ],
  },
  {
    id: "account-billing",
    title: "Account & billing",
    description: REFERRAL_PROGRAM_SUSPENDED
      ? "Subscriptions, payments and managing your account."
      : "Subscriptions, payments, referrals and managing your account.",
    articles: [
      {
        id: "manage-subscription",
        question: "How do I manage or cancel my subscription?",
        answer:
          "Head to Settings → Subscription to view your plan, switch tiers, or cancel any time. If you subscribed through the App Store or Google Play, manage it from that marketplace instead.",
        keywords: ["subscription", "cancel", "billing", "plan", "upgrade", "downgrade"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/account/manage-subscription`,
        placeholder: false,
      },
      {
        id: "update-payment",
        question: "How do I update my payment method?",
        answer:
          "Open Settings → Billing to add a new card, set your default, or remove a saved one.",
        keywords: ["payment", "card", "billing", "stripe"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/account/payment-methods`,
        placeholder: false,
      },
      {
        id: "delete-account",
        question: "How do I delete my account?",
        answer:
          "To close your account or change your login details, email support@iyuga.net and our team will take care of it — or use the contact form below and we'll get back to you.",
        keywords: ["delete", "close account", "remove", "gdpr", "data"],
        media: null,
        docHref: `${DOCS_SITE_BASE}/account/delete-account`,
        placeholder: false,
      },
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    description: "Fixes for common problems and where to get more help.",
    articles: [
      {
        id: "something-not-working",
        question: "Something isn't working — what should I do?",
        answer:
          "Start with a refresh — it fixes more than you'd think. Still stuck? Tell us what you were doing, what you expected, and what happened using the contact form below, and we'll dig in.",
        keywords: ["bug", "error", "broken", "not working", "issue", "problem"],
        media: null,
        docHref: null,
        placeholder: false,
      },
    ],
  },
];

/**
 * Flatten all static articles to a single searchable list. Each entry carries
 * its category for grouping and result labelling.
 */
export const ALL_STATIC_ARTICLES = HELP_CATEGORIES.flatMap((category) =>
  category.articles.map((article) => ({
    ...article,
    categoryId: category.id,
    categoryTitle: category.title,
  }))
);
